import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StepBasics, StepDynamic, StepTimeline, StepValues } from "../components/OnboardingSteps";

const TOTAL_STEPS = 4;

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    age: "",
    gender: "",
    location: "",
    relationship_dynamic: "",
    marriage_timeline: "",
    values: [],
    bio: "",
    photo_url: "",
  });
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const update = (patch) => setData((d) => ({ ...d, ...patch }));
  const toggleValue = (v) =>
    setData((d) => ({
      ...d,
      values: d.values.includes(v) ? d.values.filter((x) => x !== v) : [...d.values, v],
    }));

  const canNext = [
    data.age && data.gender && data.location,
    !!data.relationship_dynamic,
    !!data.marriage_timeline,
    data.values.length > 0 && data.bio.trim().length > 0,
  ][step];

  const finish = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/profile", {
        ...data,
        age: parseInt(data.age, 10),
        photo_url: data.photo_url || undefined,
      });
      setUser(updated);
      navigate("/discover");
    } finally {
      setSaving(false);
    }
  };

  const stepProps = { data, update, toggleValue };
  const steps = [
    <StepBasics key="basics" {...stepProps} />,
    <StepDynamic key="dynamic" {...stepProps} />,
    <StepTimeline key="timeline" {...stepProps} />,
    <StepValues key="values" {...stepProps} />,
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A]">
      <div className="max-w-4xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A3B3F]">
          Step {step + 1} of {TOTAL_STEPS}
        </p>
        <div className="mt-4 h-px bg-[#E5E0D8] relative">
          <div
            className="absolute inset-y-0 left-0 bg-[#7A3B3F] transition-all duration-500"
            style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
          />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="mt-16"
          >
            {steps[step]}
          </motion.div>
        </AnimatePresence>
        <div className="mt-16 flex items-center justify-between">
          <button
            data-testid="onboarding-back-button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className={`text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors duration-300 ${
              step === 0 ? "invisible" : ""
            }`}
          >
            ← Back
          </button>
          {step < TOTAL_STEPS - 1 ? (
            <button
              data-testid="onboarding-next-button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              className="bg-[#7A3B3F] text-[#FAF9F6] px-8 py-4 hover:bg-[#5D2B2E] transition-colors duration-300 disabled:opacity-40"
            >
              Continue
            </button>
          ) : (
            <button
              data-testid="onboarding-finish-button"
              onClick={finish}
              disabled={!canNext || saving}
              className="bg-[#7A3B3F] text-[#FAF9F6] px-8 py-4 hover:bg-[#5D2B2E] transition-colors duration-300 disabled:opacity-40"
            >
              {saving ? "Saving…" : "Enter Evervow"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
